import ajax from '@/lib/ajax';
import { VuexModule, Module, Mutation, Action, getModule } from 'vuex-module-decorators';
import store from '@/store';
import { AppModule } from '@/store/Auth/AppModule'
import ProductMigration from '@/models/Products/ProductMigrations/ProductMigration';
import CreateProductMigrationRequest from '@/models/Products/ProductMigrations/CreateProductMigrationRequest';
import ProductMigrationLog from '@/models/Products/ProductMigrations/ProductMigrationLog';
import Product from '@/models/Products/Product';
import ProductIssue from '@/models/Products/ProductIssues/ProductIssue';
import ProductState from '@/models/Products/ProductState';
import PermissionUtils from '@/lib/permissionUtils';

export interface IProductMigrationState {
    loading: boolean;
    loadingMigrationProducts: boolean;
    loadingMigrationProductIssues: boolean;
    loadingMigrationLog: boolean;
    productMigrations: ProductMigration[];
    productMigration: ProductMigration;
    totalProductMigrationsPages: number;
    productsForMigration: Product[];
    productIssuesForMigration: ProductIssue[];
    productMigrationLog: ProductMigrationLog[];
    totalProductMigrationLogPages: number;
    newProductMigrationId: string;
    pageSize: number;
}

@Module({ dynamic: true, store, name: 'ProductMigrationModule', namespaced: true })
class ProductMigrationStore extends VuexModule implements IProductMigrationState {

    public loading: boolean = true;
    public loadingMigrationProducts: boolean = true;
    public loadingMigrationProductIssues: boolean = true;
    public loadingMigrationLog: boolean = true;
    public productMigrations: ProductMigration[] = [];
    public productMigration: ProductMigration = new ProductMigration();
    public totalProductMigrationsPages: number = 1;
    public productsForMigration: Product[] = [];
    public productIssuesForMigration: ProductIssue[] = [];
    public newProductMigrationId: string = null;
    public productMigrationLog: ProductMigrationLog[] = [];
    public totalProductMigrationLogPages: number = 1;
    public pageSize: number = PermissionUtils.getPageSize();

    @Action
    async getProductMigrations(pageNumber: number) {

        this.setLoading(true);

        await ajax.get(`/portal-backend/fi-api/internal/migrations?pageSize=${this.pageSize}&pageNumber=${pageNumber}`)
        .then(response => {
            this.context.commit("setProductMigrations", response.data.results);
            this.context.commit("setProductMigrationsTotalPages", response.data.records.totalRecords);
            this.setLoading(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoading(false);
        });
    }

    @Action
    async getProductMigration(productMigrationId: string) {

        this.setLoading(true);

        await ajax.get(`/portal-backend/fi-api/internal/migrations/${productMigrationId}`)
        .then(response => {
            this.context.commit("setProductMigration", response.data);
            this.setLoading(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoading(false);
        });
    }

    @Action
    async createProductMigration(request: CreateProductMigrationRequest) {

        this.setLoading(true);

        await ajax.post(`/portal-backend/fi-api/internal/migrations/`, request)
        .then(response => {
            this.context.commit("setNewProductMigrationFromCreate", response.data.id);
            this.setLoading(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoading(false);
        });
    }

    @Action
    async deleteProductMigration(productMigrationId: string) {

        this.setLoading(true);

        await ajax.delete(`/portal-backend/fi-api/internal/migrations/${productMigrationId}`)
        .then(() => {
            this.setLoading(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoading(false);
        });
    }

    @Action
    async getProductsForMigration() {

        this.setLoadingMigrationProducts(true);

        await ajax.get(`/portal-backend/fi-api/internal/products`)
        .then(response => {
            this.context.commit("setProducts", response.data.results);
            this.setLoadingMigrationProducts(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingMigrationProducts(false);
        });
    }

    @Action
    async getProductIssuesForMigration(productId: string) {

        this.setLoadingMigrationProductIssues(true);
        // Always only get published product issues

        await ajax.get(`/portal-backend/fi-api/internal/product-issues?state=${ProductState.Published}&productId=${productId}`)
        .then(response => {
            this.context.commit("setProductIssues", response.data.results);
            this.setLoadingMigrationProductIssues(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingMigrationProductIssues(false);
        });
    }

    @Action
    async getProductMigrationLog(pageNumber: number) {

        this.setLoadingMigrationLog(true);

        await ajax.get(`/portal-backend/fi-api/internal/migrations/${this.productMigration.id}/logs?pageSize=${this.pageSize}&pageNumber=${pageNumber}`)
        .then(response => {
            this.context.commit("setProductMigrationLog", response.data.results);
            this.context.commit("setProductMigrationLogTotalPages", response.data.records.totalRecords);
            this.setLoadingMigrationLog(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingMigrationLog(false);
        });
    }

    @Action
    cleanState() {
        this.context.commit("resetState");
    }

    @Mutation
    resetState() {
        this.loading = true;
        this.loadingMigrationProducts = true;
        this.loadingMigrationProductIssues = true;
        this.loadingMigrationLog = true;
        this.productMigrations = [];
        this.totalProductMigrationsPages = 1;
        this.productsForMigration = [];
        this.productIssuesForMigration = [];
        this.productMigrationLog = [];
        this.totalProductMigrationLogPages = 1;
        this.newProductMigrationId = null;
    }

    @Mutation
    private async setLoading(loading: boolean) {
        this.loading = loading;
    }

    @Mutation
    private async setLoadingMigrationProducts(loading: boolean) {
        this.loadingMigrationProducts = loading;
    }

    @Mutation
    private async setLoadingMigrationProductIssues(loading: boolean) {
        this.loadingMigrationProductIssues = loading;
    }

    @Mutation
    private async setLoadingMigrationLog(loading: boolean) {
        this.loadingMigrationLog = loading;
    }

    @Mutation
    private async setProductMigrations(response: ProductMigration[]) {
        if (response) {
            this.productMigrations = response;
        }
    }

    @Mutation
    private async setProductMigration(productMigration: ProductMigration) {
        this.productMigration = productMigration;
    }

    @Mutation
    private async setProductMigrationsTotalPages(total: number) {
        this.totalProductMigrationsPages = Math.ceil(total/this.pageSize);
    }

    @Mutation
    private async setProducts(response: Product[]) {
        if (response) {
            this.productsForMigration = response;
        }
    }

    @Mutation
    private async setProductIssues(response: ProductIssue[]) {
        if (response) {
            this.productIssuesForMigration = response;
        }
    }

    @Mutation
    private async setProductMigrationLog(response: ProductMigrationLog[]) {
        if (response) {
            this.productMigrationLog = response;
        }
    }

    @Mutation
    private async setProductMigrationLogTotalPages(total: number) {
        this.totalProductMigrationLogPages = Math.ceil(total/this.pageSize);
    }

    @Mutation
    private async setNewProductMigrationFromCreate(productMigrationId: string) {
        this.newProductMigrationId = productMigrationId;
    }

}

export const ProductMigrationModule = getModule(ProductMigrationStore)
